import { useState, useEffect, createContext, useContext } from "react";
import { supabase } from "@/lib/supabase";
import type { User } from "@supabase/supabase-js";

export interface Profile {
  nome: string;
  sobrenome: string | null;
  avatar_url: string | null;
  telefone: string | null;
  bio: string | null;
  instagram: string | null;
  youtube: string | null;
  spotify: string | null;
  tiktok: string | null;
  role: string;
  payment_info: Record<string, any> | null;
  data_nascimento: string | null;
  perfil_publico: boolean;
  mostrar_whatsapp: boolean;
  endereco: Record<string, string> | null;
  email_contato: string | null;
  cpf: string | null;
  profile_locked: boolean;
}

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  isAdmin: boolean;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, nome: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
      } else {
        setLoading(false);
      }
    });

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
      } else {
        setProfile(null);
        setLoading(false);
      }
    });

    return () => {
      listener.subscription.unsubscribe();
    };
  }, []);

  async function fetchProfile(userId: string) {
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("nome, sobrenome, avatar_url, telefone, bio, instagram, youtube, spotify, tiktok, role, payment_info, data_nascimento, perfil_publico, mostrar_whatsapp, endereco, email_contato, cpf, profile_locked")
        .eq("id", userId)
        .maybeSingle();
      if (error) {
        console.error("Erro ao buscar perfil:", error.message);
        setLoading(false);
        return;
      }
      if (data) {
        setProfile({
          nome: data.nome || "",
          sobrenome: data.sobrenome || null,
          avatar_url: data.avatar_url || null,
          telefone: data.telefone || null,
          bio: data.bio || null,
          instagram: data.instagram || null,
          youtube: data.youtube || null,
          spotify: data.spotify || null,
          tiktok: data.tiktok || null,
          role: data.role || "user",
          payment_info: data.payment_info || null,
          data_nascimento: data.data_nascimento || null,
          perfil_publico: data.perfil_publico || false,
          mostrar_whatsapp: data.mostrar_whatsapp || false,
          endereco: data.endereco || null,
          email_contato: data.email_contato || null,
          cpf: data.cpf || null,
          profile_locked: data.profile_locked || false,
        });
      } else {
        console.warn("Perfil não encontrado para o usuário:", userId);
      }
    } catch (err) {
      console.error("Exceção ao buscar perfil:", err);
    }
    setLoading(false);
  }

  async function refreshProfile() {
    if (user) await fetchProfile(user.id);
  }

  async function signIn(email: string, password: string) {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error };
  }

  async function signUp(email: string, password: string, nome: string) {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { nome } },
    });
    return { error };
  }

  async function signOut() {
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        isAdmin: profile?.role === "admin" || user?.email === "farmatheus@gmail.com",
        loading,
        signIn,
        signUp,
        signOut,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}